import React from 'react';
import {
    Box,
    Heading,
    Text,
    Stack,
    List,
    ListItem,
    ListIcon,
    Button,
    Image,
    Divider,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';

const sectionStyle = {
    px: { base: 6, md: 12 },
    py: { base: 12, md: 20 },
};

const LiverDetox = () => {
    return (
        <Box fontFamily="Georgia, serif">

            {/* 🔷 Hero Banner */}
            <Box bg="#3D5940" {...sectionStyle}>
                <Box maxW="6xl" mx="auto" mt={10}>
                    <Image
                        src="/assets/images/liver-detox.png"
                        alt="Liver Detox Therapy"
                        w="100%"
                        h={{ base: '200px', md: '400px' }}
                        objectFit="cover"
                        borderRadius="xl"
                        boxShadow="lg"
                        mb={10}
                    />
                    <Heading size="lg" color="#ffffff" textAlign="center" mb={4}>
                        What is Liver Detox Therapy?
                    </Heading>
                    <Text color="#ffffff" fontSize="lg" maxW="3xl" mx="auto" textAlign="center">
                        Liver Detox Therapy is a natural and holistic process that supports the liver’s function in filtering toxins from the body. Using a blend of herbal supplements, hydration, and lifestyle recommendations, this therapy aims to enhance liver health and restore vitality by improving metabolic and digestive processes.
                    </Text>
                </Box>
            </Box>

            {/* ✅ Benefits */}
            <Box bg="#E6E9D8" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={6} textAlign="center">
                        Key Benefits
                    </Heading>
                    <List spacing={3} color="gray.700" fontSize="md">
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Improves liver and gallbladder function</ListItem>
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Boosts metabolism and fat digestion</ListItem>
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Clears skin and supports hormonal balance</ListItem>
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Reduces fatigue, sluggishness, and brain fog</ListItem>
                    </List>
                </Box>
            </Box>

            {/* 🔹 How it Works */}
            <Box bg="white" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={4}>How the Session Works</Heading>
                    <Text color="gray.700" fontSize="md">
                        Liver Detox Therapy typically begins with a consultation to assess your current liver load and symptoms. A customized detox plan is prescribed, which may include herbal tinctures, nutritional support, liver-friendly foods, hydration, and gentle cleansing protocols. Follow-up sessions track your progress and make adjustments as needed.
                    </Text>
                </Box>
            </Box>

            {/* 🔹 Who is it For? */}
            <Box bg="#F1F6EF" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={4}>Who Should Consider Liver Detox?</Heading>
                    <Text color="gray.700" fontSize="md" mb={2}>
                        This therapy is ideal for individuals who experience:
                    </Text>
                    <List spacing={2} pt={2} color="gray.700" fontSize="md">
                        <ListItem>• Frequent fatigue or low energy</ListItem>
                        <ListItem>• Digestive issues like bloating or sluggish digestion</ListItem>
                        <ListItem>• Skin problems such as acne or dullness</ListItem>
                        <ListItem>• Regular alcohol use, medications, or poor diet</ListItem>
                    </List>
                </Box>
            </Box>

            {/* 🔹 Contraindications */}
            <Box bg="#FFF9F0" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="red.700" mb={4}>Contraindications</Heading>
                    <List spacing={3} fontSize="md" color="gray.700">
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Liver cirrhosis or serious hepatic conditions</ListItem>
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Severe kidney disorders</ListItem>
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Active gallbladder infections</ListItem>
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Pregnancy or lactation (without physician clearance)</ListItem>
                    </List>
                </Box>
            </Box>

            {/* 🔹 Herbal/Nutritional Protocol */}
            <Box bg="#E6E9D8" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={4}>Our Herbal & Nutritional Approach</Heading>
                    <Text color="gray.700" fontSize="md">
                        We use carefully sourced herbs like milk thistle, dandelion root, turmeric, and triphala to gently activate liver detox pathways. Our plans are supported with high-fiber diets, antioxidant-rich smoothies, and clean hydration strategies to help your liver regenerate naturally.
                    </Text>
                </Box>
            </Box>

            {/* 🔸 CTA */}
            <Box bg="white" {...sectionStyle}>
                <Box maxW="3xl" mx="auto" textAlign="center">
                    <Heading size="md" color="green.800" mb={2}>Restore Your Energy & Cleanse Naturally</Heading>
                    <Text mb={4} color="gray.600">Book your Liver Detox session now and feel the difference from within.</Text>
                    <Button size="lg" colorScheme="green">Book Now</Button>
                </Box>
            </Box>

            <Divider mt={16} />
        </Box>
    );
};

export default LiverDetox;
